import { useGetVosOproep, usePostVosResponse } from "@/entities/medewerkers-op-station";
import {
    Button,
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    Empty,
    EmptyDescription,
    EmptyHeader,
    EmptyImage,
    ErrorDisplay,
    Icon,
    LoadingIndicator,
} from "@/shared/components";
import { isApiError } from "@/shared/utils";

type VosReactieProps = {
    token: string;
};

export function VosReactie({ token }: VosReactieProps) {
    const { data: vosOproep, isPending, error } = useGetVosOproep(token);

    const postVosResponse = usePostVosResponse();

    const respondToRequest = (gaatKomen: boolean) => {
        postVosResponse.mutate({ token, gaatKomen });
    };

    if (error) {
        if (isApiError(error) && error.status) {
            return <VosError status={error.status} message={error.message} />;
        }
        return <ErrorDisplay error={error} variant="section" />;
    }

    if (isPending || !vosOproep) {
        return <LoadingIndicator size="medium" message="VOS informatie ophalen..." fullHeight />;
    }

    switch (vosOproep.status) {
        case "NietMeerNodig":
        case "Gestopt":
            return <InzetNietMeerNodig station={vosOproep.opStation} />;
        case "Komt":
            return <KomtWel station={vosOproep.opStation} />;
        case "KomtNiet":
            return <KomtNiet />;
    }

    return (
        <Card className="mx-auto flex max-w-100 flex-col gap-2 py-4">
            <CardHeader>
                <h1 className="text-l font-medium">VOS inzet</h1>
                <CardDescription>
                    <p>Er is hulp nodig op {vosOproep.opStation}. Kun jij komen?</p>
                </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
                <Button icon="close" variant="secondary" onClick={() => respondToRequest(false)}>
                    Ik kan niet komen
                </Button>
                <Button icon="check" variant="primary" onClick={() => respondToRequest(true)}>
                    Ik kom
                </Button>
                {postVosResponse.error && <ErrorDisplay error={postVosResponse.error} />}
            </CardContent>
        </Card>
    );
}

type VosErrorProps = {
    status: number;
    message: string;
};
function VosError({ status, message }: VosErrorProps) {
    switch (status) {
        case 404:
            return (
                <div className="flex flex-col items-center justify-center p-4">
                    <Empty className="gap-2">
                        <EmptyHeader>Helaas, dat is niet gelukt</EmptyHeader>
                        <EmptyDescription>Geen informatie gevonden</EmptyDescription>
                        <EmptyImage variant="search" />
                    </Empty>
                </div>
            );
        case 409:
            return (
                <div className="flex flex-col items-center justify-center p-4">
                    <Empty className="gap-2">
                        <EmptyHeader>VOS inzet</EmptyHeader>
                        <EmptyDescription>{message}</EmptyDescription>
                    </Empty>
                </div>
            );
        case 410:
            return (
                <div className="flex flex-col items-center justify-center p-4">
                    <Empty className="gap-2">
                        <EmptyHeader>Deze link is niet meer geldig</EmptyHeader>
                        <EmptyDescription>{message}</EmptyDescription>
                    </Empty>
                </div>
            );
        default:
            return (
                <div className="flex flex-col items-center justify-center p-4">
                    <Empty className="gap-2">
                        <EmptyHeader>Helaas, daar ging iets mis</EmptyHeader>
                    </Empty>
                </div>
            );
    }
}

type InzetNietMeerNodigProps = {
    station: string;
};
function InzetNietMeerNodig({ station }: InzetNietMeerNodigProps) {
    return (
        <Card className="mx-auto my-4 flex max-w-100 flex-col gap-2 py-4">
            <CardHeader className="flex flex-row justify-between">
                <h1 className="text-l font-medium">VOS inzet</h1>
            </CardHeader>

            <CardContent>
                <span>
                    De inzet op {station} is niet meer nodig. Er is genoeg hulp, of de situatie is opgelost. Je hoeft niet te komen.
                </span>
            </CardContent>
        </Card>
    );
}

type KomtWelProps = {
    station: string;
};

function KomtWel({ station }: KomtWelProps) {
    return (
        <Card className="mx-auto my-4 flex max-w-100 flex-col gap-2 py-4">
            <CardHeader className="flex flex-row justify-between">
                <h1 className="text-l font-medium">Bedankt!</h1>
                <Icon name="thumbs-up" className="text-content-emphasis" strokeWidth="thick" />
            </CardHeader>

            <CardContent>
                <span>We rekenen op je op {station}.</span>
            </CardContent>
        </Card>
    );
}

function KomtNiet() {
    return (
        <Card className="mx-auto my-4 flex max-w-100 flex-col gap-2 py-4">
            <CardHeader className="flex flex-row justify-between">
                <h1 className="text-l font-medium">Geen probleem</h1>
            </CardHeader>

            <CardContent>
                <span>Bedankt voor je reactie. We zoeken verder naar beschikbare collega's.</span>
            </CardContent>
        </Card>
    );
}
